import React, { useRef, useEffect, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { IoDocumentTextOutline } from 'react-icons/io5';
import './ChatMessagesList.css';
import { useTranslation } from 'react-i18next';
import ChatApiService from '../../../../Services/chat.service';
import UserAvatar from '../../../Widgets/UserAvatar/UserAvatar';
import { Menu, MenuItem, ListItemIcon, ListItemText, Checkbox, Button, Stack, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplyIcon from '@mui/icons-material/Reply';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';

const ChatMessagesList = ({
  messages = [],
  containerHeight = 600,
  onClickImage,
  onScroll,
  highestPageLoaded,
  lowestPageLoaded,
  isTyping,
  getAllForReceiver,
  refreshMessages,
  onReplyToMessage,
  onScrollToIndex,
  scrollContainerRef,
  onRepliedMessageClick,
  onForwardMessages,
  onAttachMessages,
  attachActionLabel,
  enableSelectionActions = true,
  allowDeleteSelected = true,
  showParticipantNames = false,
  defaultMultiSelectMode = false,
  hideExitMultiSelect = false,
}) => {
  const internalRef = useRef(null);
  const parentRef = scrollContainerRef || internalRef;
  const { i18n, t } = useTranslation();
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [multiSelectMode, setMultiSelectMode] = useState(!!defaultMultiSelectMode);

  useEffect(() => {
    if (defaultMultiSelectMode) {
      setMultiSelectMode(true);
    }
  }, [defaultMultiSelectMode]);

  useEffect(() => {
    // Drop selections that no longer exist after pagination/filter changes.
    setSelectedMessages((prev) => {
      if (!prev.size) return prev;
      const validIds = new Set(messages.map((m) => m.id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [messages]);

  useEffect(() => {
    const parent = parentRef.current;
    if (!parent || !onScroll) return;

    const handleScroll = () => {
      const scrollTop = parent.scrollTop;
      const scrollHeight = parent.scrollHeight;
      const clientHeight = parent.clientHeight;

      // Pass the scroll data to the onScroll handler
      onScroll({ scrollTop, scrollHeight, clientHeight });
    };

    parent.addEventListener('scroll', handleScroll);
    return () => parent.removeEventListener('scroll', handleScroll);
  }, [onScroll]);

  const handleRightClick = (event, message) => {
    if (!enableSelectionActions) return;
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      message,
    });
    handleToggleMessageSelection(message.id);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleToggleMessageSelection = (messageId) => {
    const newSelectedMessages = new Set(selectedMessages);
    if (newSelectedMessages.has(messageId)) {
      newSelectedMessages.delete(messageId);
    } else {
      newSelectedMessages.add(messageId);
    }
    setSelectedMessages(newSelectedMessages);
  };

  const handleEnterMultiSelectMode = () => {
    setMultiSelectMode(true);
    setSelectedMessages(new Set([contextMenu.message.id])); // Start with the right-clicked message
    handleCloseContextMenu();
  };

  const handleDeleteSelectedMessages = async () => {
    if (!allowDeleteSelected) return;
    try {
      if (!selectedMessages || selectedMessages.size === 0) {
        console.warn("No messages selected for deletion.");
        return;
      }

      await Promise.all(
        Array.from(selectedMessages).map((messageId) => {
          if (!messageId) {
            console.warn("Encountered an undefined message ID.");
            return Promise.resolve(); // Skip undefined IDs
          }
          return ChatApiService.deleteMessage(messageId);
        })
      );

      setSelectedMessages(new Set());
      setMultiSelectMode(false);
      refreshMessages(false);
    } catch (error) {
      console.error("Error deleting messages:", error);
    }
  };

  const handleExitMultiSelectMode = () => {
    setSelectedMessages(new Set());
    if (!defaultMultiSelectMode) {
      setMultiSelectMode(false);
    }
  };

  const handleForwardSelectedMessages = () => {
    if (!selectedMessages || selectedMessages.size === 0) return;
    const selected = messages.filter((msg) => selectedMessages.has(msg.id));
    if (onForwardMessages) {
      onForwardMessages(selected);
    }
    setSelectedMessages(new Set());
    setMultiSelectMode(false);
    handleCloseContextMenu();
  };

  const handleAttachSelectedMessages = () => {
    if (!selectedMessages || selectedMessages.size === 0) return;
    const selected = messages.filter((msg) => selectedMessages.has(msg.id));
    if (onAttachMessages) {
      onAttachMessages(selected);
    }
    setSelectedMessages(new Set());
    if (!defaultMultiSelectMode) {
      setMultiSelectMode(false);
    }
    handleCloseContextMenu();
  };
  // Setup the virtualizer
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100, // Initial size estimate
    overscan: 5,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  // Scroll to the bottom when the component loads or the messages update
  useEffect(() => {
    if (virtualizer && highestPageLoaded === 1 && lowestPageLoaded === 1) {
      setTimeout(() => {
        virtualizer.scrollToIndex(messages.length - 1, {
          align: 'end',
          behavior: 'smooth',
        });
      }, 100); // Allow time for DOM updates
    }
  }, [messages, virtualizer]);

  useEffect(() => {
    if (onScrollToIndex) {
      onScrollToIndex.current = (index) => {
        virtualizer.scrollToIndex(index, { align: 'center' });
      };
    }
  }, [onScrollToIndex, virtualizer]);

  const handleReplyToMessage = (message) => {
    // This sets the "replyingMessage" in Chat’s parent state
    if (onReplyToMessage) {
      onReplyToMessage(message);
    }
    handleCloseContextMenu();
  };
  return (
    <div
      ref={parentRef}
      className="chat-messages-container"
      style={{
        height: containerHeight,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >

        {virtualizer.getVirtualItems().map((virtualRow) => {
          const message = messages[virtualRow.index];
          if (!message) return null;

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="chat-message-row"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
              onContextMenu={(e) => handleRightClick(e, message)}
              onClick={() => {
                if (multiSelectMode) {
                  handleToggleMessageSelection(message.id);
                }
              }}
            >
              {multiSelectMode && (
                <Checkbox
                  checked={selectedMessages.has(message.id)}
                  onChange={() => handleToggleMessageSelection(message.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    [message.position === 'right' ? 'left' : 'right']: '10px', // Dynamic positioning
                    top: '10px',
                  }}
                />
              )}
              <MessageBubble
                message={message}
                onClickImage={onClickImage}
                t={t}
                getAllForReceiver={getAllForReceiver}
                showParticipantNames={showParticipantNames}
                onRepliedMessageClick={onRepliedMessageClick}
              />
            </div>
          );
        })}
      </div>
      {enableSelectionActions && (
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {!multiSelectMode && [
            <MenuItem key="reply" onClick={() => handleReplyToMessage(contextMenu.message)}>
              <ListItemIcon>
                <ReplyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Reply')}</ListItemText>
            </MenuItem>,
            ...(onForwardMessages ? [(
              <MenuItem key="forward" onClick={handleForwardSelectedMessages}>
                <ListItemIcon>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('Forward')}</ListItemText>
              </MenuItem>
            )] : []),
            <MenuItem key="select" onClick={handleEnterMultiSelectMode}>
              <ListItemIcon>
                <SelectAllIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('SelectMultipleMessages')}</ListItemText>
            </MenuItem>
          ]}
          {onAttachMessages && (
            <MenuItem onClick={handleAttachSelectedMessages}>
              <ListItemIcon>
                <LinkIcon fontSize="small" sx={{ color: '#E57C22' }} />
              </ListItemIcon>
              <ListItemText>{attachActionLabel || t('AttachConversation')}</ListItemText>
            </MenuItem>
          )}
          {onForwardMessages && (
            <MenuItem onClick={handleForwardSelectedMessages}>
              <ListItemIcon>
                <ShareIcon fontSize="small" sx={{ color: '#304FFF' }} />
              </ListItemIcon>
              <ListItemText>{t('Forward')}</ListItemText>
            </MenuItem>
          )}
          {allowDeleteSelected && (
            <MenuItem onClick={handleDeleteSelectedMessages}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'red' }} />
              </ListItemIcon>
              <ListItemText>{t('DeleteSelected')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}
      {enableSelectionActions && multiSelectMode && selectedMessages.size > 0 && (
        <div className="bulk-actions" dir={i18n.dir()}>
          <div className="bulk-actions-inner">
            <Chip
              size="small"
              label={`${selectedMessages.size} ${t('Selected')}`}
              className="bulk-actions-count"
            />
            <Stack direction="row" spacing={1} className="bulk-actions-buttons">
              {onAttachMessages && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={handleAttachSelectedMessages}
                  className="bulk-action-btn bulk-action-btn-primary"
                >
                  {attachActionLabel || t('AttachConversation')}
                </Button>
              )}
              {onForwardMessages && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  onClick={handleForwardSelectedMessages}
                  className="bulk-action-btn bulk-action-btn-secondary"
                >
                  {t('Forward')}
                </Button>
              )}
              {allowDeleteSelected && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelectedMessages}
                  className="bulk-action-btn bulk-action-btn-danger"
                >
                  {t('DeleteSelected')}
                </Button>
              )}
              {(!hideExitMultiSelect && !defaultMultiSelectMode) && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={handleExitMultiSelectMode}
                  className="bulk-action-btn bulk-action-btn-ghost"
                >
                  {t('ExitSelectionMode')}
                </Button>
              )}
              {(defaultMultiSelectMode || hideExitMultiSelect) && selectedMessages.size > 0 && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={handleExitMultiSelectMode}
                  className="bulk-action-btn bulk-action-btn-ghost"
                >
                  {t('ClearSelection') || t('ExitSelectionMode')}
                </Button>
              )}
            </Stack>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessagesList;

/* --------------------------------------
   MessageBubble: Renders a single message
   -------------------------------------- */

const renderReadAt = (isRight, receiverReadAt, t) => {
  if (receiverReadAt && isRight) {
    const readDate = new Date(receiverReadAt);
    const now = new Date();
    const isDifferentDay =
      readDate.getDate() !== now.getDate() ||
      readDate.getMonth() !== now.getMonth() ||
      readDate.getFullYear() !== now.getFullYear();

    const formattedDate = isDifferentDay
      ? `${readDate.getDate().toString().padStart(2, '0')}:${(readDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')} ${readDate
          .getHours()
          .toString()
          .padStart(2, '0')}:${readDate.getMinutes().toString().padStart(2, '0')}`
      : `${readDate.getHours().toString().padStart(2, '0')}:${readDate
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;

    return (
      <div className="read-at-container">
        <span className="read-at-text">
          {t('ReadAt')}: {formattedDate}
        </span>
      </div>
    );
  }
  return null;
};



function MessageBubble({ message, onClickImage, t, getAllForReceiver, showParticipantNames, onRepliedMessageClick }) {
  const { position, title, dateString, sending, receiverReadAt, senderName, receiverName } = message;
  const isRight = position === 'right';
  const senderLabel = senderName || title || '';
  const receiverLabel = receiverName || '';

  return (
    <div
      className={`bubble-row ${isRight ? 'bubble-row-left' : 'bubble-row-right'}`}
      style={{ display: 'flex', columnGap: '10px' }}
    >

      <div>
        {showParticipantNames && (senderLabel || receiverLabel) && (
          <div className={`participant-names ${isRight ? 'participant-names-right' : 'participant-names-left'}`}>
            {senderLabel && (
              <span>
                <strong>{t('Sender')}:</strong> {senderLabel}
              </span>
            )}
            {receiverLabel && (
              <span>
                <strong>{t('Receiver')}:</strong> {receiverLabel}
              </span>
            )}
          </div>
        )}
        {message.replyTo && (
          <div
            className={`reply-preview-container ${isRight ? 'reply-preview-right' : 'reply-preview-left'}`}
            onClick={() => onRepliedMessageClick?.(message.replyTo.id)}
          >
            <div className="reply-bar" />
            <div className="reply-content">
              <span className="reply-title">{t('ReplyingTo')}</span>
              <span className="reply-text">
                {message.replyTo.text || t('MediaMessage')}
              </span>
            </div>
          </div>
        )}
        <div className={`bubble-container ${isRight ? 'bubble-right' : 'bubble-left'}`}>
          {renderMessageContent(message, onClickImage, t)}
          <div className="bubble-footer">
            <div className={isRight ? 'bubble-timestamp' : 'bubble-timestamp-receiver'}>
              {dateString}
            </div>
            {isRight && (
              <div className="bubble-status">
                {sending === true && <span className="status-sending">{t('Sending')}</span>}
                {/* {sending === false && <span className="status-success">✔ Sent</span>} */}
                {sending === 'failed' && <span className="status-failed">{t('FailedToSend')}</span>}
              </div>
            )}
          </div>

        </div>
        {renderReadAt(isRight, receiverReadAt, t)}
      </div>
      {((!isRight && getAllForReceiver) || (showParticipantNames && !isRight)) && (

        <div className="sender-name2">
          <UserAvatar name={senderLabel || 'Unknown'} size={25} withName={false} />
          <div className="sender-name">
            {senderLabel}
          </div>
        </div>
      )}
    </div>
  );
}

function renderMessageContent(message, onClickImage, t) {
  const { type, text, data } = message;

  switch (type) {
    case 'text':
      return <TextBubble text={text} />;
    case 'photo':
      return <PhotoBubble uris={data?.uris || []} onClickImage={onClickImage} />;
    case 'file':
      return <FileBubble fileURLs={data?.fileURLs || []} t={t} />;
    case 'audio':
      return <AudioBubble audioURLs={data?.audioURLs || []} t={t} />;
    default:
      return <TextBubble text={text} />;
  }
}

/* --------------------------------
   Helper Functions
   -------------------------------- */
function TextBubble({ text = '' }) {
  // Function to detect and render clickable links
  const renderTextWithLinks = (text) => {
    // URL regex pattern
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Split text by URLs
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      // Check if this part is a URL
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 'bold',
              cursor: 'pointer',
              wordBreak: 'break-all'
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = '0.8';
              e.target.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = '1';
              e.target.style.textDecoration = 'underline';
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent bubble click events
            }}
          >
            {part}
          </a>
        );
      }
      // Regular text
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="bubble-content text-bubble" style={{ whiteSpace: 'pre-wrap' }}>
      {renderTextWithLinks(text)}
    </div>
  );
}

function PhotoBubble({ uris, onClickImage }) {

  return (
    <div className="bubble-content photo-bubble">
      {uris.map((uri, idx) => (
        <img
          key={`${uri}-${idx}`}
          src={uri}
          alt={`img-${idx}`}
          className="photo-image"
          onClick={() => onClickImage?.(uri)}
        />
      ))}
    </div>
  );
}

/* --------------------------------
   Updated FileBubble with FilePreview
   -------------------------------- */
function FileBubble({ fileURLs, t }) {
  return (
    <div className="bubble-content file-bubble">
      {fileURLs.map((fileUrl, idx) => (
        <FilePreview key={`${fileUrl}-${idx}`} fileUrl={fileUrl} t={t} />
      ))}
    </div>
  );
}
/* FilePreview Component */
function FilePreview({ fileUrl: initialFileUrl, t }) {

  // const fileName = extractFileName(fileUrl);
  const { fileUrl, fileName } = extractFileDetails(initialFileUrl);
  const fileExt = getFileExtension(fileName).toLowerCase();

  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(fileExt);
  const isPdf = fileExt === 'pdf';
  const isVideo = ['mp4', 'webm', 'ogg'].includes(fileExt);
  const isAudio = ['mp3', 'wav', 'ogg'].includes(fileExt);
  const isText = ['txt', 'csv'].includes(fileExt);

  const handleFileOpen = () => {

    window.open(fileUrl, '_blank');
  };

  return (
    <div className="file-item" >
      {isImage && (
        <img
          src={fileUrl}
          alt={fileName}
          className="file-preview image-preview"
          onClick={handleFileOpen}
        />
      )}
      {isPdf && (
        <div className="file-preview pdf-preview">
          <iframe
            title={`pdf-preview-${fileName}`}
            src={fileUrl}
            className="iframe-preview"
          />
          <div
            className="chat-iframe-overlay"
            onClick={handleFileOpen}
            style={{ cursor: 'pointer' }}
            title="Click to open PDF"
          />
        </div>

      )}
      {isVideo && (
        <video
          controls
          src={fileUrl}
          className="file-preview video-preview"
          onClick={handleFileOpen}
        >
          {t('BrowserDoesNotSupportVideo')}
        </video>
      )}
      {/* {isAudio && (
        <audio
          controls
          src={fileUrl}
          className="file-preview audio-preview"
          onClick={handleFileOpen}
        >
          {t('BrowserDoesNotSupportAudio')}
        </audio>
      )} */}
      {isText && (
        <div
          className="file-preview text-preview"
          onClick={handleFileOpen}
          title={fileName}
        >
          <IoDocumentTextOutline size={30} color="#304FFF" />
          <span className="file-name">{fileName}</span>
        </div>
      )}
      {!isImage && !isPdf && !isVideo && !isAudio && !isText && (
        <div
          className="file-preview unsupported-preview"
          onClick={handleFileOpen}
        >
          <IoDocumentTextOutline size={30} color="#304FFF" />
          <span className="file-name">{fileName}</span>
        </div>
      )}
    </div>
  );
}

const NUM_WAVEFORM_BARS = 35;

function generateWaveform(id, numBars) {
  let seed = 0;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    seed = ((seed << 5) - seed) + str.charCodeAt(i);
    seed = seed & seed;
  }
  const bars = [];
  for (let i = 0; i < numBars; i++) {
    seed = Math.abs((seed * 16807 + 12345) % 2147483647);
    const val = (seed % 1000) / 1000;
    bars.push(0.12 + val * 0.88);
  }
  return bars;
}

function AudioWaveformPlayer({ audioUrl, id }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const waveformRef = useRef(null);
  const [waveformBars] = useState(() => generateWaveform(id || audioUrl, NUM_WAVEFORM_BARS));
  const playbackRates = [0.5, 1.0, 1.5, 2.0];

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const handleLoadedMetadata = () => {
    if (audioRef.current && isFinite(audioRef.current.duration)) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setPosition(0);
    clearInterval(timerRef.current);
  };

  const playPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      clearInterval(timerRef.current);
    } else {
      audio.playbackRate = playbackRate;
      audio.play().catch(err => console.error('Playback failed:', err));
      setIsPlaying(true);
      timerRef.current = setInterval(() => {
        if (audio && isFinite(audio.currentTime)) {
          setPosition(audio.currentTime);
        }
      }, 100);
    }
  };

  const handleWaveformClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration || !waveformRef.current) return;
    const rect = waveformRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, x / rect.width));
    audio.currentTime = progress * duration;
    setPosition(audio.currentTime);
  };

  const toggleSpeed = () => {
    const idx = playbackRates.indexOf(playbackRate);
    const next = playbackRates[(idx + 1) % playbackRates.length];
    setPlaybackRate(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = next;
    }
  };

  const progress = duration > 0 ? position / duration : 0;

  return (
    <div className="waveform-player">
      {/* Keep a real DOM audio element for reliable cross-format playback */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        style={{ display: 'none' }}
      />
      <button className="waveform-play-btn" onClick={playPause}>
        {isPlaying ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><polygon points="5,3 19,12 5,21" /></svg>
        )}
      </button>
      <div className="waveform-body">
        <div className="waveform-bars" ref={waveformRef} onClick={handleWaveformClick}>
          {waveformBars.map((height, index) => {
            const barProgress = (index + 0.5) / waveformBars.length;
            const isActive = barProgress <= progress;
            return (
              <div
                key={index}
                className={`waveform-bar ${isActive ? 'active' : ''}`}
                style={{ height: `${Math.max(3, height * 24)}px` }}
              />
            );
          })}
        </div>
        <span className="waveform-time">
          {isPlaying ? formatTime(position) : formatTime(duration)}
        </span>
      </div>
      <button className="waveform-speed-btn" onClick={toggleSpeed}>
        {playbackRate}x
      </button>
    </div>
  );
}

function AudioBubble({ audioURLs, t }) {
  return (
    <div className="bubble-content audio-bubble">
      {audioURLs.map((audioUrl, idx) => (
        <AudioWaveformPlayer key={`${audioUrl}-${idx}`} audioUrl={audioUrl} id={`${audioUrl}-${idx}`} />
      ))}
    </div>
  );
}

function extractFileName(url) {
  if (url.startsWith('blob:') && url.includes('|')) {
    // Split the URL by the '|' character
    const parts = url.split('|');
    // Return the part after the '|', which contains the file name
    return parts[1];
  }

  const parts = url.split('/');
  return parts[parts.length - 1].split('?')[0] || 'Unknown file';
}

function extractFileDetails(fileUrl) {
  let fileName = ''; // Default file name extraction
  if (fileUrl.startsWith('blob:') && fileUrl.includes('|')) {
    const parts = fileUrl.split('|');
    fileUrl = parts[0]; // Extract the blob URL
    fileName = parts[1]; // Extract the file name
  }
  else {
    const parts = fileUrl.split('/');
    fileName = parts[parts.length - 1].split('?')[0] || 'Unknown file';
  }
  return { fileUrl, fileName };
}

function getFileExtension(fileName) {
  return fileName.split('.').pop().toLowerCase();
}

